/**
 * Command palette (Cmd/Ctrl+K) — Linear/Vercel-tier quick nav + actions.
 *
 * Opens via keyboard shortcut. Fuzzy search over routes. Press ↑↓ to
 * navigate, Enter to activate, Esc to close.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, LayoutGrid, Sparkles, Image as ImageIcon,
  Calendar as CalendarIcon, Briefcase, Layers, Users, Heart, Building2,
  Settings, Crown, LogOut, Sun, Moon,
} from "lucide-react";
import { ROUTES } from "@/types";
import { useTheme } from "@/hooks/useTheme";
import authStore from "@/lib/auth";

interface Cmd {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  group: "Navigate" | "Actions";
  shortcut?: string;
  run: () => void;
}

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  // Toggle on cmd/ctrl+k
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const commands: Cmd[] = useMemo(() => {
    const close = () => setOpen(false);
    const go = (path: string) => () => { close(); navigate(path); };
    return [
      { id: "go-dashboard",   label: "Go to Explore",     group: "Navigate", icon: <LayoutGrid    size={14} strokeWidth={1.5} />, shortcut: "G D", run: go(ROUTES.DASHBOARD) },
      { id: "go-studio",      label: "Open Studio",       group: "Navigate", icon: <Sparkles      size={14} strokeWidth={1.5} />, shortcut: "G S", run: go(ROUTES.STUDIO) },
      { id: "go-library",     label: "Browse Library",    group: "Navigate", icon: <ImageIcon     size={14} strokeWidth={1.5} />, shortcut: "G L", run: go(ROUTES.LIBRARY) },
      { id: "go-calendar",    label: "Calendar",          group: "Navigate", icon: <CalendarIcon  size={14} strokeWidth={1.5} />, shortcut: "G C", run: go(ROUTES.CALENDAR) },
      { id: "go-brandkit",    label: "Brand kit",         group: "Navigate", icon: <Briefcase     size={14} strokeWidth={1.5} />, shortcut: "G B", run: go(ROUTES.BRAND_KIT) },
      { id: "go-templates",   label: "Templates",         group: "Navigate", icon: <Layers        size={14} strokeWidth={1.5} />, shortcut: "G T", run: go(ROUTES.TEMPLATES) },
      { id: "go-team",        label: "Team",              group: "Navigate", icon: <Users         size={14} strokeWidth={1.5} />, run: go(ROUTES.TEAM) },
      { id: "go-inspiration", label: "Saved inspiration", group: "Navigate", icon: <Heart         size={14} strokeWidth={1.5} />, run: go(ROUTES.INSPIRATION) },
      { id: "go-workspaces",  label: "Manage workspaces", group: "Navigate", icon: <Building2     size={14} strokeWidth={1.5} />, run: go(ROUTES.WORKSPACES) },
      { id: "go-settings",    label: "Settings",          group: "Navigate", icon: <Settings      size={14} strokeWidth={1.5} />, run: go(ROUTES.SETTINGS) },
      { id: "go-plans",       label: "Plans & billing",   group: "Navigate", icon: <Crown         size={14} strokeWidth={1.5} />, run: go(ROUTES.PLANS) },
      { id: "act-theme",      label: `Switch to ${theme === "light" ? "dark" : "light"} theme`,
                                                          group: "Actions", icon: theme === "light" ? <Moon size={14} strokeWidth={1.5} /> : <Sun size={14} strokeWidth={1.5} />,
                              run: () => { close(); toggleTheme(); } },
      { id: "act-logout",     label: "Sign out",          group: "Actions", icon: <LogOut        size={14} strokeWidth={1.5} />, run: () => { close(); authStore.clear(); } },
    ];
  }, [navigate, theme, toggleTheme]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      c.label.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Reset active index when filter narrows
  useEffect(() => { setActive(0); }, [query]);

  // Keyboard nav within palette
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter")     { e.preventDefault(); filtered[active]?.run(); }
  };

  if (!open) return null;

  // Group filtered for display
  const groups: Record<string, Cmd[]> = {};
  for (const c of filtered) (groups[c.group] ||= []).push(c);

  let runningIdx = -1;

  return createPortal(
    <div
      role="dialog"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0,
        zIndex: "var(--z-command)" as unknown as number,
        background: "var(--bg-overlay)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        padding: "12vh 16px 16px",
        animation: "fadeIn 160ms var(--ease-smooth)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 560,
          background: "var(--bg-surface-raised)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-modal)",
          overflow: "hidden",
          animation: "scaleIn 200ms var(--ease-smooth)",
        }}
      >
        {/* Search input */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 18px",
          borderBottom: "1px solid var(--border-default)",
        }}>
          <Search size={16} strokeWidth={1.5} style={{ color: "var(--text-tertiary)" }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type a command or search…"
            style={{
              flex: 1,
              background: "transparent", border: "none", outline: "none",
              fontSize: "var(--font-size-md)",
              color: "var(--text-primary)",
              fontFamily: "var(--font-family)",
            }}
          />
          <kbd style={{
            fontFamily: "var(--font-mono)", fontSize: 10,
            color: "var(--text-tertiary)",
            padding: "2px 6px",
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
          }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: 6 }}>
          {filtered.length === 0 && (
            <div style={{
              padding: 32, textAlign: "center",
              color: "var(--text-tertiary)",
              fontSize: "var(--font-size-sm)",
            }}>
              No matches for "{query}"
            </div>
          )}
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <div style={{
                padding: "8px 12px 4px",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--text-tertiary)",
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-label)",
              }}>
                {group}
              </div>
              {items.map((c) => {
                runningIdx++;
                const isActive = runningIdx === active;
                return (
                  <button
                    key={c.id}
                    onMouseEnter={() => setActive(filtered.findIndex((f) => f.id === c.id))}
                    onClick={c.run}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      width: "100%", padding: "10px 12px",
                      background: isActive ? "var(--bg-surface-alt)" : "transparent",
                      border: "none", borderRadius: "var(--radius-md)",
                      textAlign: "left", cursor: "pointer",
                      fontSize: "var(--font-size-sm)",
                      color: "var(--text-primary)",
                      transition: "background 80ms",
                    }}
                  >
                    <span style={{ color: "var(--text-tertiary)", display: "inline-flex" }}>{c.icon}</span>
                    <span style={{ flex: 1, fontWeight: 500 }}>{c.label}</span>
                    {c.shortcut && (
                      <kbd style={{
                        fontFamily: "var(--font-mono)", fontSize: 10,
                        color: "var(--text-tertiary)",
                        padding: "2px 6px",
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-default)",
                        borderRadius: 4,
                      }}>
                        {c.shortcut}
                      </kbd>
                    )}
                    {isActive && <ArrowRight size={12} strokeWidth={1.5} style={{ color: "var(--text-tertiary)" }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 14px",
          borderTop: "1px solid var(--border-default)",
          background: "var(--bg-surface-alt)",
          fontSize: "var(--font-size-xs)",
          color: "var(--text-tertiary)",
          fontFamily: "var(--font-mono)",
        }}>
          <span>↑↓ · ↵ · esc</span>
          <span>{filtered.length}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;
